// § 3.15 Tier 1 — project Notifications page. Manages the
// "Add Notification Webhook" modal: kind picker, URL paste with
// shape-specific validation hints, event checklist, Test button
// (synchronous round-trip to Slack/Teams), and Save (creates the row
// and reloads the page). Also handles the per-row kebab actions
// (Send test, Re-enable, Delete-with-confirm).
//
// § 3.15 Tier 3 Step 27 — when the project's company has installed the
// Slack OAuth app, the Slack tab swaps the URL-paste field for a
// channel picker. Channels lazy-load via GET .../slack_channels and
// filter client-side as the user types.
import { Controller } from "@hotwired/stimulus"

const SLACK_REGEX = /^https:\/\/hooks\.slack\.com\/services\/T[A-Z0-9]+\/B[A-Z0-9]+\/[A-Za-z0-9]+$/
const TEAMS_REGEX = /^https:\/\/prod-\d+\.[a-z0-9-]+\.logic\.azure\.com:443\/workflows\/[a-f0-9-]+\/triggers\/manual\/paths\/invoke\?[^\s]*sig=[^\s]+$/i
const O365_HOST = "outlook.office.com"

export default class extends Controller {
  static values = {
    createUrl: String,
    updateUrlTemplate: String,
    destroyUrlTemplate: String,
    testUrlTemplate: String,
    deliveriesUrlTemplate: String,
    testUrlPreflight: String,
    slackChannelsUrl: String,
    slackOauthAvailable: Boolean
  }

  static targets = [
    "backdrop", "modal",
    "form", "kindRadio", "urlInput", "urlError", "urlHint",
    "urlPanel", "labelInput", "eventsContainer",
    "testButton", "testStatus", "submitButton",
    "deleteBackdrop", "deleteModal", "deleteLabel",
    "logBackdrop", "logDrawer", "logBody",
    "slackPicker", "slackChannelSearch", "slackChannelDropdown",
    "slackChannelLoading", "slackChannelList", "slackChannelEmpty",
    "slackChannelError", "slackPickedHint", "slackPickedLabel",
    "slackChannelIdInput", "slackChannelNameInput"
  ]

  connect() {
    this._pendingDeleteId = null
    this._slackChannels = null      // cached channel list, lazy-loaded
    this._slackPickerOverride = false
    document.addEventListener("click", this._handleOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this._handleOutsideClick)
  }

  _handleOutsideClick = (event) => {
    if (!this.hasSlackChannelDropdownTarget) return
    if (this.slackChannelDropdownTarget.classList.contains("hidden")) return
    if (this.element.contains(event.target)) return
    this.slackChannelDropdownTarget.classList.add("hidden")
  }

  // ── Modal show/hide ────────────────────────────────────────
  openCreateModal() {
    this._resetForm()
    this._showModal()
  }

  hideModal(event) {
    if (event && event.target !== event.currentTarget) return
    this.modalTarget.classList.add("hidden")
    this.backdropTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _showModal() {
    this.backdropTarget.classList.remove("hidden")
    this.modalTarget.classList.remove("hidden")
  }

  // ── Kind / URL change handling ─────────────────────────────
  kindChanged() {
    this._slackPickerOverride = false
    this._refreshPanels()
    this._refreshUrlValidation()
    this._refreshUrlHint()
    this._refreshTestButtonState()
    if (this._isSlackOauthSelected()) this._loadSlackChannels()
  }

  // Stimulus action — flips the Slack tab from picker to URL paste
  // for customers who prefer the legacy flow even with the OAuth app
  // installed.
  showUrlFallback() {
    this._slackPickerOverride = true
    // Flip the kind radio from slack_oauth → slack so the form posts
    // a paste-URL row. The DOM input on the kind radio carries the
    // slack_oauth value when the OAuth app is installed; toggle it
    // back to "slack" here.
    const radio = this.kindRadioTargets.find((r) => r.dataset.slackTab === "true")
    if (radio) radio.value = "slack"
    this._refreshPanels()
    this._refreshUrlHint()
    this._refreshUrlValidation()
    this._refreshTestButtonState()
  }

  urlChanged() {
    this._refreshUrlValidation()
    this._refreshTestButtonState()
  }

  _selectedKind() {
    const radio = this.kindRadioTargets.find((r) => r.checked)
    return radio ? radio.value : "slack"
  }

  _isSlackOauthSelected() {
    return this._selectedKind() === "slack_oauth" && !this._slackPickerOverride
  }

  _refreshPanels() {
    const slackOauth = this._isSlackOauthSelected()
    if (this.hasSlackPickerTarget) {
      this.slackPickerTarget.classList.toggle("hidden", !slackOauth)
    }
    if (this.hasUrlPanelTarget) {
      this.urlPanelTarget.classList.toggle("hidden", slackOauth)
    }
  }

  _refreshUrlHint() {
    if (!this.hasUrlHintTarget) return
    const kind = this._selectedKind()
    if (kind === "slack" || kind === "slack_oauth") {
      this.urlHintTarget.textContent =
        "Paste a Slack incoming-webhook URL (https://hooks.slack.com/services/…)."
    } else {
      this.urlHintTarget.textContent =
        "Paste a Microsoft Teams Workflow URL (https://prod-NN.<region>.logic.azure.com:443/workflows/…)."
    }
  }

  _refreshUrlValidation() {
    if (!this.hasUrlInputTarget) return
    if (this._isSlackOauthSelected()) {
      this.urlErrorTarget.classList.add("hidden")
      return
    }
    const kind = this._selectedKind() === "slack_oauth" ? "slack" : this._selectedKind()
    const url = this.urlInputTarget.value.trim()
    const error = this._validateUrl(kind, url)
    if (error) {
      this.urlErrorTarget.textContent = error
      this.urlErrorTarget.classList.remove("hidden")
    } else {
      this.urlErrorTarget.classList.add("hidden")
    }
  }

  _refreshTestButtonState() {
    if (!this.hasTestButtonTarget) return
    if (this._isSlackOauthSelected()) {
      // The /test_url endpoint preflights paste-URL rows; for
      // slack_oauth the per-row Test kebab fires post-save. Disable
      // the in-modal Test button + tell the user via the test-status
      // line so they don't think the button is broken.
      this.testButtonTarget.disabled = true
      if (this.hasTestStatusTarget && this.hasSlackChannelIdInputTarget) {
        const picked = !!this.slackChannelIdInputTarget.value
        if (picked && this.testStatusTarget.classList.contains("hidden")) {
          this._setTestStatus("Save first, then use the kebab menu's Send test.", "info")
        }
      }
      return
    }
    const kind = this._selectedKind()
    const url = this.hasUrlInputTarget ? this.urlInputTarget.value.trim() : ""
    if (!url) {
      this.testButtonTarget.disabled = true
      return
    }
    const error = this._validateUrl(kind, url)
    this.testButtonTarget.disabled = !!error
  }

  _validateUrl(kind, url) {
    if (!url) return null
    let parsed
    try { parsed = new URL(url) } catch { return "Not a valid URL." }
    if (parsed.protocol !== "https:") return "Must use https://."
    if (parsed.host.toLowerCase() === O365_HOST) {
      return "Microsoft retired the legacy Office 365 Connectors on May 22, 2026. " +
             "Please regenerate this URL using the Workflows app in Microsoft Teams."
    }
    if (kind === "slack" && !SLACK_REGEX.test(url)) {
      return "Doesn't look like a Slack incoming-webhook URL."
    }
    if (kind === "teams_workflow" && !TEAMS_REGEX.test(url)) {
      return "Doesn't look like a Microsoft Teams Workflow URL."
    }
    return null
  }

  // ── Slack channel picker (Tier 3 Step 27) ──────────────────
  async _loadSlackChannels() {
    if (!this.hasSlackChannelsUrlValue || !this.slackChannelsUrlValue) return
    if (this._slackChannels) {
      this._renderSlackChannelList()
      return
    }
    this._showSlackPickerLoading()
    try {
      const res = await fetch(this.slackChannelsUrlValue, {
        credentials: "same-origin",
        headers: { "Accept": "application/json", "X-CSRF-Token": this._csrfToken() }
      })
      const body = await res.json().catch(() => ({}))
      if (res.ok && body && body.ok) {
        this._slackChannels = Array.isArray(body.channels) ? body.channels : []
        this._renderSlackChannelList()
      } else {
        this._showSlackPickerError(body && (body.message || body.error) || `HTTP ${res.status}`)
      }
    } catch (e) {
      this._showSlackPickerError(e.message || "Network error")
    }
  }

  _showSlackPickerLoading() {
    if (this.hasSlackChannelLoadingTarget) this.slackChannelLoadingTarget.classList.remove("hidden")
    if (this.hasSlackChannelListTarget) this.slackChannelListTarget.classList.add("hidden")
    if (this.hasSlackChannelEmptyTarget) this.slackChannelEmptyTarget.classList.add("hidden")
    if (this.hasSlackChannelErrorTarget) this.slackChannelErrorTarget.classList.add("hidden")
  }

  _showSlackPickerError(message) {
    if (this.hasSlackChannelLoadingTarget) this.slackChannelLoadingTarget.classList.add("hidden")
    if (this.hasSlackChannelListTarget) this.slackChannelListTarget.classList.add("hidden")
    if (this.hasSlackChannelEmptyTarget) this.slackChannelEmptyTarget.classList.add("hidden")
    if (this.hasSlackChannelErrorTarget) {
      this.slackChannelErrorTarget.textContent = `✗ ${message}`
      this.slackChannelErrorTarget.classList.remove("hidden")
    }
  }

  openSlackChannelDropdown() {
    if (!this.hasSlackChannelDropdownTarget) return
    this.slackChannelDropdownTarget.classList.remove("hidden")
    if (!this._slackChannels) this._loadSlackChannels()
  }

  filterSlackChannels() {
    this.openSlackChannelDropdown()
    this._renderSlackChannelList()
  }

  _renderSlackChannelList() {
    if (!this._slackChannels) return
    if (this.hasSlackChannelLoadingTarget) this.slackChannelLoadingTarget.classList.add("hidden")
    if (this.hasSlackChannelErrorTarget) this.slackChannelErrorTarget.classList.add("hidden")
    if (!this.hasSlackChannelListTarget) return

    const query = this.hasSlackChannelSearchTarget
      ? this.slackChannelSearchTarget.value.trim().toLowerCase()
      : ""
    const matches = this._slackChannels.filter((c) => {
      if (c.is_archived) return false
      if (!query) return true
      return c.name && c.name.toLowerCase().includes(query)
    })

    if (matches.length === 0) {
      this.slackChannelListTarget.classList.add("hidden")
      if (this.hasSlackChannelEmptyTarget) this.slackChannelEmptyTarget.classList.remove("hidden")
      return
    }

    if (this.hasSlackChannelEmptyTarget) this.slackChannelEmptyTarget.classList.add("hidden")
    this.slackChannelListTarget.classList.remove("hidden")
    this.slackChannelListTarget.innerHTML = ""
    matches.slice(0, 200).forEach((c) => {
      const row = document.createElement("button")
      row.type = "button"
      row.className = "w-full text-left px-3 py-2 text-[13px] text-gray-900 hover:bg-gray-50 flex items-center gap-2"
      const lock = c.is_private ? "🔒 " : "# "
      row.textContent = `${lock}${c.name}`
      row.dataset.channelId = c.id
      row.dataset.channelName = c.name
      row.addEventListener("click", () => this._pickSlackChannel(c.id, c.name))
      this.slackChannelListTarget.appendChild(row)
    })
  }

  _pickSlackChannel(id, name) {
    if (this.hasSlackChannelIdInputTarget) this.slackChannelIdInputTarget.value = id
    if (this.hasSlackChannelNameInputTarget) this.slackChannelNameInputTarget.value = name
    if (this.hasSlackChannelSearchTarget) this.slackChannelSearchTarget.value = `#${name}`
    if (this.hasSlackPickedHintTarget) this.slackPickedHintTarget.classList.remove("hidden")
    if (this.hasSlackPickedLabelTarget) this.slackPickedLabelTarget.textContent = `#${name}`
    if (this.hasSlackChannelDropdownTarget) this.slackChannelDropdownTarget.classList.add("hidden")
  }

  // ── Test button (preflight, no DB row needed) ──────────────
  async runTest() {
    if (this._isSlackOauthSelected()) return // disabled by _refreshTestButtonState
    const kind = this._selectedKind()
    const url  = this.urlInputTarget.value.trim()
    const label = this.labelInputTarget.value.trim()
    if (this._validateUrl(kind, url)) return

    this._setTestStatus("Sending test…", "info")
    this.testButtonTarget.disabled = true

    try {
      const result = await this._postJSON(this.testUrlPreflightValue, {
        kind, url, label
      })
      if (result.ok && result.body && result.body.ok) {
        this._setTestStatus("✓ Sent — check your channel.", "ok")
      } else {
        const msg = (result.body && result.body.error) || `HTTP ${(result.body && result.body.http_status) || result.status}`
        this._setTestStatus(`✗ ${msg}`, "error")
      }
    } catch (e) {
      this._setTestStatus(`✗ ${e.message || "Request failed"}`, "error")
    } finally {
      this.testButtonTarget.disabled = false
    }
  }

  _setTestStatus(text, kind) {
    this.testStatusTarget.classList.remove("hidden", "text-rose-600", "text-emerald-600", "text-gray-500")
    this.testStatusTarget.textContent = text
    if (kind === "ok") this.testStatusTarget.classList.add("text-emerald-600")
    else if (kind === "error") this.testStatusTarget.classList.add("text-rose-600")
    else this.testStatusTarget.classList.add("text-gray-500")
  }

  // ── Save (create row) ──────────────────────────────────────
  async submit(event) {
    event.preventDefault()
    const kind = this._selectedKind()
    const eventTypes = Array.from(
      this.eventsContainerTarget.querySelectorAll('input[name="incoming_chat_webhook[event_types][]"]:checked')
    ).map((cb) => cb.value)
    if (eventTypes.length === 0) {
      this._setTestStatus("Pick at least one event before saving.", "error")
      return
    }

    const payload = {
      incoming_chat_webhook: {
        kind,
        label: this.labelInputTarget.value.trim(),
        event_types: eventTypes
      }
    }

    if (this._isSlackOauthSelected()) {
      const channelId = this.hasSlackChannelIdInputTarget ? this.slackChannelIdInputTarget.value.trim() : ""
      const channelName = this.hasSlackChannelNameInputTarget ? this.slackChannelNameInputTarget.value.trim() : ""
      if (!channelId) {
        this._setTestStatus("Pick a Slack channel before saving.", "error")
        return
      }
      payload.incoming_chat_webhook.slack_channel_id = channelId
      payload.incoming_chat_webhook.slack_channel_name = channelName
    } else {
      const url = this.hasUrlInputTarget ? this.urlInputTarget.value.trim() : ""
      const error = this._validateUrl(kind, url)
      if (error) {
        if (this.hasUrlErrorTarget) {
          this.urlErrorTarget.textContent = error
          this.urlErrorTarget.classList.remove("hidden")
        }
        return
      }
      payload.incoming_chat_webhook.url = url
    }

    this.submitButtonTarget.disabled = true
    try {
      const result = await this._postJSON(this.createUrlValue, payload)
      if (result.ok && result.body && result.body.ok) {
        window.location.reload()
      } else {
        const msg = (result.body && (result.body.errors || []).join(", ")) || "Save failed."
        this._setTestStatus(`✗ ${msg}`, "error")
        this.submitButtonTarget.disabled = false
      }
    } catch (e) {
      this._setTestStatus(`✗ ${e.message || "Save failed"}`, "error")
      this.submitButtonTarget.disabled = false
    }
  }

  // ── Per-row kebab actions ──────────────────────────────────
  async openTestModal(event) {
    const id = event.params.id
    const url = this.testUrlTemplateValue.replace("ID", id)
    try {
      const result = await this._postJSON(url, {})
      if (result.ok && result.body && result.body.ok) {
        alert("✓ Sent — check your channel.")
      } else {
        const msg = (result.body && result.body.error) || `HTTP ${result.status}`
        alert(`✗ ${msg}`)
      }
    } catch (e) {
      alert(`✗ ${e.message || "Request failed"}`)
    }
  }

  async reenable(event) {
    const id = event.params.id
    const url = this.updateUrlTemplateValue.replace("ID", id)
    try {
      const result = await this._patchJSON(url, { reenable: "true" })
      if (result.ok && result.body && result.body.ok) {
        window.location.reload()
      } else {
        alert("Re-enable failed.")
      }
    } catch (e) {
      alert(`Re-enable failed: ${e.message || ""}`)
    }
  }

  openDeleteModal(event) {
    this._pendingDeleteId = event.params.id
    if (this.hasDeleteLabelTarget) {
      this.deleteLabelTarget.textContent = event.params.label || "this webhook"
    }
    this.deleteBackdropTarget.classList.remove("hidden")
    this.deleteModalTarget.classList.remove("hidden")
  }

  hideDeleteModal(event) {
    if (event && event.target !== event.currentTarget) return
    this.deleteBackdropTarget.classList.add("hidden")
    this.deleteModalTarget.classList.add("hidden")
    this._pendingDeleteId = null
  }

  async confirmDelete() {
    if (!this._pendingDeleteId) return
    const url = this.destroyUrlTemplateValue.replace("ID", this._pendingDeleteId)
    try {
      const result = await this._deleteJSON(url)
      if (result.ok) {
        window.location.reload()
      } else {
        alert("Delete failed.")
      }
    } catch (e) {
      alert(`Delete failed: ${e.message || ""}`)
    }
  }

  // ── Delivery log drawer (Tier 2 Step 20) ───────────────────
  async openDeliveryLog(event) {
    const id = event.params.id
    if (!id || !this.deliveriesUrlTemplateValue) return
    const url = this.deliveriesUrlTemplateValue.replace("ID", id)
    this.logBackdropTarget.classList.remove("hidden")
    this.logDrawerTarget.classList.remove("hidden")
    this.logBodyTarget.innerHTML =
      '<div class="px-5 py-10 text-center text-[12px] text-gray-500">Loading…</div>'
    try {
      const res = await fetch(url, {
        credentials: "same-origin",
        headers: { "Accept": "text/html", "X-CSRF-Token": this._csrfToken() }
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const html = await res.text()
      this.logBodyTarget.innerHTML = html
    } catch (e) {
      this.logBodyTarget.innerHTML =
        `<div class="px-5 py-10 text-center text-[12px] text-rose-700">Failed to load: ${e.message || ""}</div>`
    }
  }

  hideDeliveryLog() {
    this.logBackdropTarget.classList.add("hidden")
    this.logDrawerTarget.classList.add("hidden")
  }

  // ── Utilities ──────────────────────────────────────────────
  _resetForm() {
    this.formTarget.reset()
    this._slackPickerOverride = false
    if (this.hasUrlErrorTarget) this.urlErrorTarget.classList.add("hidden")
    this.testStatusTarget.classList.add("hidden")
    this.testStatusTarget.textContent = ""
    this.testButtonTarget.disabled = true
    this.submitButtonTarget.disabled = false
    if (this.hasSlackChannelIdInputTarget) this.slackChannelIdInputTarget.value = ""
    if (this.hasSlackChannelNameInputTarget) this.slackChannelNameInputTarget.value = ""
    if (this.hasSlackChannelSearchTarget) this.slackChannelSearchTarget.value = ""
    if (this.hasSlackPickedHintTarget) this.slackPickedHintTarget.classList.add("hidden")
    if (this.hasSlackChannelDropdownTarget) this.slackChannelDropdownTarget.classList.add("hidden")
    this._refreshPanels()
    this._refreshUrlHint()
    this._refreshTestButtonState()
    if (this._isSlackOauthSelected()) this._loadSlackChannels()
  }

  _csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]')
    return el ? el.content : ""
  }

  async _postJSON(url, payload) {
    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this._csrfToken()
      },
      body: JSON.stringify(payload)
    })
    let body = null
    try { body = await res.json() } catch { /* non-JSON response */ }
    return { ok: res.ok, status: res.status, body }
  }

  async _patchJSON(url, payload) {
    const res = await fetch(url, {
      method: "PATCH",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this._csrfToken()
      },
      body: JSON.stringify(payload)
    })
    let body = null
    try { body = await res.json() } catch {}
    return { ok: res.ok, status: res.status, body }
  }

  async _deleteJSON(url) {
    const res = await fetch(url, {
      method: "DELETE",
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this._csrfToken()
      }
    })
    let body = null
    try { body = await res.json() } catch {}
    return { ok: res.ok, status: res.status, body }
  }
}
